import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Users, Lock, Mail, Phone, Building2, Clock, 
  UserCheck, Shield, Eye
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReferenceEntry } from '@/types/jobWizard';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';
import { motion } from 'framer-motion';

interface JobReferencesListProps {
  references: ReferenceEntry[];
  canAccess?: boolean;
  onRequestAccess?: () => void;
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  supervisor: 'Direct Supervisor',
  colleague: 'Colleague',
  client: 'Client',
  instructor: 'Instructor',
  mentor: 'Mentor',
  professional: 'Professional Contact',
  other: 'Other',
};

const JobReferencesList: React.FC<JobReferencesListProps> = ({
  references,
  canAccess = false,
  onRequestAccess,
}) => {
  if (!references || references.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          Professional References
          <Badge variant="secondary" className="ml-auto text-xs">
            {references.length} {references.length === 1 ? 'Reference' : 'References'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-2">
          {references.map((ref, index) => {
            const isLocked = ref.isConfidential && !canAccess;
            
            return (
              <motion.div
                key={ref.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={cn(
                  "relative p-4 rounded-xl border transition-all",
                  isLocked 
                    ? "bg-muted/30 border-dashed" 
                    : "bg-gradient-to-br from-background to-muted/20 hover:shadow-md hover:border-primary/30"
                )}
              >
                {/* Confidential Badge */}
                {ref.isConfidential && (
                  <div className="absolute top-2 right-2">
                    {canAccess ? (
                      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] gap-0.5">
                        <Eye className="w-2.5 h-2.5" />
                        Unlocked
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px] gap-0.5">
                        <Lock className="w-2.5 h-2.5" />
                        Private
                      </Badge>
                    )}
                  </div>
                )}

                {/* Reference Info */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <UserCheck className="w-5 h-5 text-primary" />
                  </div>

                  <div className="flex-1 min-w-0 pr-12">
                    <h4 className="font-semibold text-sm truncate">{ref.name}</h4>
                    
                    {ref.title && (
                      <p className="text-xs text-muted-foreground truncate">{ref.title}</p>
                    )}
                    
                    {ref.company && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Building2 className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{ref.company}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Relationship & Years Known */}
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant="outline" className="text-[10px]">
                    {RELATIONSHIP_LABELS[ref.relationship] || ref.relationship}
                  </Badge>
                  {ref.yearsKnown && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {ref.yearsKnown}+ years
                    </span>
                  )}
                </div>

                {/* Contact Info */}
                {(ref.email || ref.phone) && (
                  <div className="mt-3 pt-3 border-t space-y-1.5">
                    {ref.email && (
                      <div className="flex items-center gap-2 text-xs">
                        <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                        {isLocked ? (
                          <BlurredPrivateData
                            placeholderContent={<span className="text-muted-foreground">email@hidden.com</span>}
                            fieldName="Email"
                            size="sm"
                            inline
                          />
                        ) : (
                          <a 
                            href={`mailto:${ref.email}`} 
                            className="text-primary hover:underline truncate"
                          >
                            {ref.email}
                          </a>
                        )}
                      </div>
                    )}
                    {ref.phone && (
                      <div className="flex items-center gap-2 text-xs">
                        <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                        {isLocked ? (
                          <BlurredPrivateData
                            placeholderContent={<span className="text-muted-foreground">+1 (XXX) XXX-XXXX</span>}
                            fieldName="Phone"
                            size="sm"
                            inline
                          />
                        ) : (
                          <a 
                            href={`tel:${ref.phone}`} 
                            className="text-primary hover:underline"
                          >
                            {ref.phone}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Request Access Button */}
        {references.some(r => r.isConfidential) && !canAccess && onRequestAccess && (
          <Button 
            variant="outline" 
            className="w-full mt-4 gap-2"
            onClick={onRequestAccess}
          >
            <Shield className="w-4 h-4" />
            Request Access to Private References
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default JobReferencesList;
